import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding,
  EventEmitter, ElementRef, Input, OnInit, OnDestroy, Output, ViewChild,
} from '@angular/core';
import * as _ from 'lodash';
import { Observable } from 'rxjs';

import { ActiveFilterModel } from '../active-filters/active-filter.model';
import { VCDB_FILTER_TYPE } from '../active-filters/active-filter-types';

import { FitmentsService } from '../../fitments/fitments.service';
import { BrandService } from '../../brand/brand.service';
import { AdvancedSearchService } from '../advanced-search/advanced-search.service';


@Component({
  moduleId: module.id,
  selector: 'vehicle-attributes-search',
  templateUrl: 'vehicle-attributes-search.component.html',
  styleUrls: ['vehicle-attributes-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehicleAttributesSearchComponent implements OnDestroy {

  @Input() filters: Array<ActiveFilterModel> = []; // list of active filters
  @Input() ghostValue: string;

  /*
    * Attribute groups structure:
    {
      title: 'Air & Fuel'
      fields: [
        {
          attribute_name: 'aspirationName',
          title: 'Aspiration'
          optionsList: [
            option: 5,
            title: 'Naturally Aspirated',
            selected: false
          ]
        },
        {...}
      ]
    },
    {
      title: 'Engine',
      fields: [...]
    }
  */
  @Input() attributeGroups: Array<any> = []; // groups of all Attributes
  @Input() allowFilterWithoutOptions: boolean = true;
  @Input() allowSelectAllOptions: boolean = false;

  @Output() fetchOptions: EventEmitter<any> = new EventEmitter();
  @Output() optionSelected: EventEmitter<any> = new EventEmitter();
  @Output() allOptionsSelected: EventEmitter<any> = new EventEmitter();

  isActive: boolean = true;
  activeGroup: any;
  showSpinner: boolean = false;

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get brandId(): string {
    return this.brandService.getBrand();
  }

  get activeVcdbFilters(): Array<ActiveFilterModel> {
    return _.filter(this.filters, (filter: any) => {
      return filter.type === VCDB_FILTER_TYPE;
    });
  }

  get selectedVcdbFilters(): Array<ActiveFilterModel> {
    return _.reduce(this.attributeGroups, (result: Array<ActiveFilterModel>, group: any) => {
      const vcdbFilters = _.reduce(group.fields, (filtersList: Array<ActiveFilterModel>, attribute: any) => {
        // if attribute isn't selected - don't add it
        if (!attribute.selected) { return filtersList; }

        // if it's not allowed to select filter without options
        // and attribute selected but no option is selected -
        // don't add it
        if (!this.allowFilterWithoutOptions &&
          attribute.optionsList &&
          !_.find(attribute.optionsList, 'selected')) {
          return filtersList;
        }

        const filter = this.createVcdbFilter(attribute);
        if (filter) { filtersList.push(filter); }
        return filtersList;
      }, []);
      return _.concat(result, vcdbFilters);
    }, []);
  }

  get hasActiveGroup(): boolean {
    return !!_.get(this, 'activeGroup');
  }

  constructor(
    private ref: ChangeDetectorRef,
    private fitmentsService: FitmentsService,
    private brandService: BrandService,
    private advancedSearchService: AdvancedSearchService,
  ) {}

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnDestroy() {
    this.resetData();
    this.isActive = false;
    this.ref.detach();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onAttributeGroupSelect(group: any) {
    this.selectAttributeGroup(group);
  }

  onAttributeSelect(attribute: any) {
    this.selectAttribute(attribute);
  }

  onOptionSelected(option: any) {
    this.optionSelected.emit(option);
  }

  onAllOptionsSelected(options: any) {
    this.allOptionsSelected.emit(options);
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public resetData(): void {
    this.attributeGroups = [];
    this.activeGroup = null;
  }

  public resetSelectedData(): void {
    this.activeGroup = null;
    _.each(this.attributeGroups, (group: any) => {
      const selectedAttributes: Array<any> = _.filter(group.fields, ['selected', true]);
      _.each(selectedAttributes, (attribute: any) => {
        attribute.selected = false;
        attribute.optionsList = null;
      });
    });
    this.ref.markForCheck();
  }

  public setOptions(data: any, attributeName: string): void {
    const attribute = this.findAttributeInGroup(attributeName);
    if (attribute) {
      attribute.optionsList = data;
    }
    // try to set selected options from active filter
    const activeFilter = this.findActiveFilterWithAttribute(attributeName);
    if (activeFilter) {
      _.each(activeFilter.data.options, (item: any) => {
        const _attribute = this.findAttributeInGroup(attributeName);
        if (!_.get(_attribute, 'optionsList')) { _.set(_attribute, 'optionsList', data); }
        const option = _.find(_attribute.optionsList, ['option', item.option]);
        _.set(option, 'selected', true);
      });
    }
    this.ref.detectChanges();
  }

  /*
    * Iterate throught active vcdb filters
    * Set selected attributes options as active in the groups
  */
  public setActiveFiltersData() {
    _.each(this.activeVcdbFilters, (activeFilter: ActiveFilterModel) => {
      // find attribute in the group
      const attributeName = _.get(activeFilter, 'data.attributeInfo.attribute_name');
      const attribute = this.findAttributeInGroup(attributeName);
      if (attribute) {
        attribute.selected = true;
        this.fetchOptions.emit(attribute.attribute_name);
      }
    });
  }

  /*
    * Remove active filter attribute from group
  */
  public handleFilterRemove(filter: ActiveFilterModel) {
    const attributeName = _.get(filter, 'data.attributeInfo.attribute_name');
    const attribute = this.findAttributeInGroup(attributeName);
    if (attribute) {
      attribute.selected = false;
      attribute.optionsList = null;
      this.ref.detectChanges();
    }
  }

  public getGroupSelectedAttributesSize(group: any): number {
    return _.size(_.filter((group.fields), ['selected', true]));
  }

  public selectFirstAttributeGroup() {
    this.selectAttributeGroup(this.attributeGroups[0]);
  }

  public getFiltersData(): any {
    return this.attributeGroups;
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  /*
    * Output structure example
    * [
        {
          _id,
          title: 'Naturally Aspirated, Supercharged',
          type: VCDB_FILTER_TYPE,
          data: {
            attributeInfo: {attribute_name, column_name, etc..}
            options: [
              {option: 1, title: 'Naturally Aspirated'},
              {option: 2, title: 'Supercharged'}
            ]
          }
        }
     ]
  */
  private createVcdbFilter(attribute: any): ActiveFilterModel {
    const selectedOpitons = _.filter(attribute.optionsList, ['selected', true]);
    if (!selectedOpitons.length) {
      return new ActiveFilterModel(
        `${attribute.attribute_name}-${this.activeVcdbFilters.length}`,
        attribute.title,
        VCDB_FILTER_TYPE,
        { attributeInfo: _.omit(attribute, ['optionsList']), options: [] },
      );
    } else {
      return _.reduce(selectedOpitons, (filterModel: ActiveFilterModel, option: any) => {
        if (!filterModel) {
          return new ActiveFilterModel(
            `${attribute.attribute_name}-${this.activeVcdbFilters.length}`,
            option.title,
            VCDB_FILTER_TYPE,
            { attributeInfo: _.omit(attribute, ['optionsList']), options: selectedOpitons },
          );
        } else {
          filterModel.title += `, ${option.title}`;
          return filterModel;
        }
      }, null);
    }
  }

  private findActiveFilterWithAttribute(attributeName: string): ActiveFilterModel {
    return _.find(this.activeVcdbFilters, (activeFilter: ActiveFilterModel) => {
      return _.get(activeFilter, 'data.attributeInfo.attribute_name') === attributeName;
    });
  }

  private findAttributeInGroup(attributeName: string): any {
    let attribute;
    _.some(this.attributeGroups, (group: any) => {
      attribute = _.find(group.fields, ['attribute_name', attributeName]);
      if (attribute) {
        return true;
      }
      return false;
    });
    return attribute;
  }

  private findAttributeGroup(attributeName: string): any {
    return _.find(this.attributeGroups, (group: any) => {
      return _.find(group.fields, ['attribute_name', attributeName]);
    });
  }

  private selectAttribute(attribute: any) {
    attribute.selected = !attribute.selected;
    if (attribute.selected) {
      this.ref.detectChanges();
      this.fetchOptions.emit(attribute.attribute_name);
    } else {
      attribute.optionsList = null;
      this.ref.detectChanges();
    }
  }

  private selectAttributeGroup(group: any) {
    const activeGroup = _.find(this.attributeGroups, ['active', true]);
    if (activeGroup) { activeGroup.active = false; }
    group.active = !group.active;
    if (group.active) {
      this.activeGroup = group;
    } else {
      this.activeGroup = null;
    }
    this.ref.detectChanges();
  }
}
